import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, addDoc, getDocs, doc, serverTimestamp, increment, runTransaction } from 'firebase/firestore';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';
import AutocompleteInput from './AutocompleteInput';
import AverageCostPurchase from './AverageCostPurchase';
import { PackagePlus, Scale } from 'lucide-react';

const Purchase = () => {
  const [tab, setTab] = useState('new'); // 'new' | 'average'

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', maxWidth: '520px', margin: '0 auto 20px' }}>
        <button
          onClick={() => setTab('new')}
          style={{
            flex: 1, padding: '12px', borderRadius: '15px', border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            background: tab === 'new' ? 'linear-gradient(45deg, #4a00e0, #8e2de2)' : '#eef1fb',
            color: tab === 'new' ? 'white' : '#666', transition: '0.2s'
          }}
        >
          <PackagePlus size={16} /> New Purchase
        </button>
        <button
          onClick={() => setTab('average')}
          style={{
            flex: 1, padding: '12px', borderRadius: '15px', border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            background: tab === 'average' ? 'linear-gradient(45deg, #4a00e0, #8e2de2)' : '#eef1fb',
            color: tab === 'average' ? 'white' : '#666', transition: '0.2s'
          }}
        >
          <Scale size={16} /> Average Cost Purchase
        </button>
      </div>

      {tab === 'new' ? <NewPurchase /> : <AverageCostPurchase />}
    </div>
  );
};

const NewPurchase = () => {
  const [formData, setFormData] = useState({
    name: '',
    businessType: BUSINESS_TYPES[0],
    category: '',
    costPrice: '',
    sellingPrice: '', 
    quantity: '', 
    otherCost: '', 
    barcode: '', 
    supplierName: '', 
    supplierPhone: '' 
  });
  
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewPurchase, setPreviewPurchase] = useState(null);

  useEffect(() => {
    getDocs(collection(db, "products")).then(sn => setAllProducts(sn.docs.map(d => d.data())));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNameChange = (val) => {
    setFormData(prev => ({ ...prev, name: val }));
    // শুধু বর্তমানে সিলেক্ট করা Business Type-এর প্রোডাক্টের সাথেই মিলানো হচ্ছে,
    // যাতে অন্য লাইনের একই নামের প্রোডাক্ট থেকে ভুল category/দাম চলে না আসে
    const found = allProducts.find(p => p.name.toLowerCase() === val.toLowerCase().trim() && getBusinessType(p) === formData.businessType);
    if (found) {
      setFormData(prev => ({
        ...prev,
        name: val,
        businessType: getBusinessType(found),
        category: found.category || '',
        sellingPrice: found.price || '',
        costPrice: found.costPrice || ''
      }));
    }
  };

  const qtyNum = parseFloat(formData.quantity) || 0;
  const baseRateNum = parseFloat(formData.costPrice) || 0;
  const otherCostNum = Math.max(0, parseFloat(formData.otherCost) || 0);

  const perUnitExtra = qtyNum > 0 ? Math.round(otherCostNum / qtyNum) : 0;
  const finalUnitCost = baseRateNum + perUnitExtra;
  const finalTotalCost = (baseRateNum * qtyNum) + otherCostNum;

  const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

  const getFormattedDateTime = () => {
    const d = new Date();
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).replace(/ /g, '-');
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date}, ${time}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.costPrice || !formData.quantity) {
      alert("Please fill in the product name, buying rate, and quantity!");
      return;
    }

    setLoading(true);
    const currentUser = auth.currentUser;
    const addedByName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Sohan';
    const localISOTime = new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString();

    const ref = collection(db, "products");
    // ---- একই প্রোডাক্ট আগে থেকেই আছে কিনা খোঁজা হচ্ছে -- নাম case-insensitive ভাবে
    // মেলানো হয়, ঠিক যেভাবে উপরের handleNameChange অটোফিল করার সময় মেলায়। আগে এখানে
    // সরাসরি Firestore-এ where("name","==",...) (case-sensitive) কোয়েরি হতো, যেটা
    // সামান্য capitalization ভিন্ন হলেই (যেমন "katan silk" বনাম "Katan Silk") পুরনো
    // প্রোডাক্ট খুঁজে না পেয়ে একই আইটেমের একটা ডুপ্লিকেট প্রোডাক্ট বানিয়ে ফেলতো। ----
    const allSnap = await getDocs(ref);
    const nameLower = formData.name.trim().toLowerCase();
    const matchDoc = allSnap.docs.find(dc => {
      const pd = dc.data();
      return (pd.name || '').toLowerCase().trim() === nameLower && getBusinessType(pd) === formData.businessType;
    });

    const entry = { 
      date: localISOTime, 
      costPrice: finalUnitCost, // এই ব্যাচের আসল দাম -- গড় নয়
      baseRate: baseRateNum,
      otherCost: otherCostNum,
      sellingPrice: Number(formData.sellingPrice), 
      quantity: qtyNum,
      totalCost: finalTotalCost,
      addedBy: addedByName,
      supplierName: formData.supplierName.trim() || 'General Supplier',
      supplierPhone: formData.supplierPhone.trim() || 'N/A'
    };

    if (matchDoc) {
      // ---- প্রোডাক্টটা আগে থেকেই আছে: স্টক আর কেনা দাম দুটোই আগের আর নতুন ব্যাচ মিলিয়ে
      // গড় করে আপডেট হচ্ছে (Average Cost Purchase ট্যাবের মতোই), সরাসরি ওভাররাইট নয় --
      // আগে এখানে costPrice: finalUnitCost দিয়ে সরাসরি বসিয়ে দেওয়া হতো, যেটা পুরনো
      // স্টকের আসল কেনা দাম মুছে ফেলে শুধু শেষ ব্যাচের দামে বসিয়ে দিত, ফলে প্রফিট হিসাব
      // ভুল হয়ে যেত। runTransaction ব্যবহার করা হচ্ছে যাতে কাছাকাছি সময়ে দুইটা Purchase
      // সেভ হলেও স্টক/history/দাম সবসময় সার্ভারের সর্বশেষ ডেটার উপর ভিত্তি করে হিসাব হয়। ----
      await runTransaction(db, async (transaction) => {
        const productRef = doc(db, "products", matchDoc.id);
        const freshSnap = await transaction.get(productRef);
        const existing = freshSnap.exists() ? freshSnap.data() : matchDoc.data();
        const existingStock = Number(existing.stock || 0);
        const existingCost = Number(existing.costPrice || 0);
        const combinedStock = existingStock + qtyNum;
        const blendedCost = combinedStock > 0
          ? Math.round((existingStock * existingCost + qtyNum * finalUnitCost) / combinedStock)
          : finalUnitCost;
        transaction.update(productRef, {
          stock: increment(qtyNum),
          price: Number(formData.sellingPrice),
          costPrice: blendedCost,
          history: [...(existing.history || []), entry]
        });
      });
    } else {
      await addDoc(ref, {
        name: formData.name.trim(),
        businessType: formData.businessType,
        category: formData.category,
        price: Number(formData.sellingPrice), 
        costPrice: finalUnitCost,
        stock: qtyNum, 
        barcode: formData.barcode || '',
        history: [entry], 
        createdAt: serverTimestamp() 
      });
    }

    let purchaseDocId = null;
    // Date.now() (base36) -- same scheme as Sales.js's invoiceId -- so two purchase
    // vouchers can't end up sharing the same reference number the way a plain 6-digit
    // random number eventually could.
    const purchaseIdShort = "PUR-" + Date.now().toString(36).toUpperCase();

    try {
      // মাস্টার রিপোর্টে সাপ্লায়ারের নাম ও অন্যান্য সঠিক ডেটা পাঠানোর জন্য
      const purchaseDoc = await addDoc(collection(db, "purchases"), {
        purchaseId: purchaseIdShort,
        productName: formData.name.trim(),
        businessType: formData.businessType,
        category: formData.category,
        costPrice: finalUnitCost,
        baseRate: baseRateNum,
        otherCost: otherCostNum,
        sellingPrice: Number(formData.sellingPrice),
        quantity: qtyNum,
        totalCost: finalTotalCost,
        supplierName: formData.supplierName.trim() || 'General Supplier',
        supplierPhone: formData.supplierPhone.trim() || 'N/A',
        addedBy: addedByName,
        date: localISOTime,
        createdAt: serverTimestamp()
      });
      purchaseDocId = purchaseDoc.id;
    } catch (err) {
      console.error("Error saving purchase report:", err);
    }

    // Print/Preview এর জন্য ইনভয়েস ডেটা তৈরি করা হলো
    setPreviewPurchase({
      purchaseId: purchaseIdShort,
      docId: purchaseDocId,
      productName: formData.name.trim(),
      category: formData.category,
      baseRate: baseRateNum,
      otherCost: otherCostNum,
      perUnitExtra: perUnitExtra,
      finalUnitCost: finalUnitCost,
      quantity: qtyNum,
      totalCost: finalTotalCost,
      supplierName: formData.supplierName.trim() || 'General Supplier',
      supplierPhone: formData.supplierPhone.trim() || 'N/A',
      addedBy: addedByName,
      dateTime: getFormattedDateTime()
    });

    setFormData({
      name: '',
      businessType: BUSINESS_TYPES[0],
      category: '',
      costPrice: '',
      sellingPrice: '', 
      quantity: '', 
      otherCost: '', 
      barcode: '', 
      supplierName: '', 
      supplierPhone: '' 
    }); 
    setLoading(false);
  };

  const handlePrintPurchase = () => {
    if (!previewPurchase) return;
    const printWindow = window.open('', '_blank', 'width=600,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>${previewPurchase.purchaseId}</title>
          <style>
            body { font-family: 'Poppins', sans-serif; padding: 20px; color: #333; }
            .invoice-box { max-width: 400px; margin: auto; padding: 20px; border: 1px solid #eee; box-shadow: 0 0 10px rgba(0,0,0,0.1); font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { padding: 5px; text-align: left; border-bottom: 1px solid #eee; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
          </style>
        </head>
        <body>
          <div class="invoice-box">
            <div style="text-align:center; margin-bottom:15px;">
              <h2 style="margin:0; color:#4a00e0; font-size:18px;">PB KIZUNA COMPANY LIMITED</h2>
              <p style="margin:4px 0; font-size:10px;">H-10, R-2, Section-2, Block-D, Aftabnagar, Dhaka</p>
              <p style="margin:0; font-size:10px;">Mobile: 01884-711844</p>
              <hr style="border: dashed 1px #000; margin: 10px 0;"/>
              <h4 style="margin:0; font-size:12px;">PURCHASE VOUCHER / INVOICE</h4>
            </div>
            <p style="margin: 2px 0;"><b>Added By:</b> ${previewPurchase.addedBy}</p>
            <p style="margin: 2px 0;"><b>Date:</b> ${previewPurchase.dateTime}</p>
            <p style="margin: 2px 0;"><b>Purchase ID:</b> #${previewPurchase.purchaseId}</p>
            <p style="margin: 2px 0;"><b>Supplier:</b> ${previewPurchase.supplierName} ${previewPurchase.supplierPhone !== 'N/A' ? `(${previewPurchase.supplierPhone})` : ''}</p>
            <hr style="border: dashed 1px #000; margin: 10px 0;"/>
            <table>
              <thead>
                <tr>
                  <th>ITEM</th>
                  <th class="text-center">QTY</th>
                  <th class="text-right">UNIT COST</th>
                  <th class="text-right">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><b>${previewPurchase.productName}</b></td>
                  <td class="text-center">${previewPurchase.quantity}</td>
                  <td class="text-right">${formatTk(previewPurchase.finalUnitCost)}</td>
                  <td class="text-right">${formatTk(previewPurchase.totalCost)}</td>
                </tr>
              </tbody>
            </table>
            <div style="margin-top:15px; text-align:right; font-size:12px;">
              <p style="margin:2px 0;">Base Rate (x${previewPurchase.quantity}): ৳ ${formatTk(previewPurchase.baseRate * previewPurchase.quantity)}</p>
              ${previewPurchase.otherCost > 0 ? `<p style="margin:2px 0;">Transport / Other Cost: +৳ ${formatTk(previewPurchase.otherCost)}</p>` : ''}
              <h3 style="margin:5px 0; font-size:14px;">Total Cost: ৳ ${formatTk(previewPurchase.totalCost)}</h3>
            </div>
            <p style="text-align:center; margin-top:20px; font-weight:bold; font-size:11px;">Thank you!</p>
          </div>
          <script>
            document.title = "${previewPurchase.purchaseId}";
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ---- সিলেক্ট করা Business Type-এর প্রোডাক্ট ছাড়া বাকিগুলো সাজেশনে দেখানো হবে না ----
  const productsOfType = allProducts.filter(p => getBusinessType(p) === formData.businessType);
  const categories = [...new Set(productsOfType.map(p => p.category).filter(Boolean))];
  // Category-ও যদি বাছাই করা থাকে, তাহলে Item-এর সাজেশন শুধু ঐ Category-র মধ্যেই সীমাবদ্ধ থাকবে
  const itemNameOptions = [...new Set(
    productsOfType
      .filter(p => !formData.category || (p.category || '').toLowerCase().trim() === formData.category.toLowerCase().trim())
      .map(p => p.name)
      .filter(Boolean)
  )];
  const currentUserName = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Staff';

  return (
    <div style={{ display:'flex', justifyContent:'center', padding:'20px' }}>
      <form onSubmit={submit} className="white-box" style={{ width:'100%', maxWidth:'500px' }}>
        <h2 style={{textAlign:'center', color:'#4a00e0', marginBottom:'4px'}}>Purchase & Stock In</h2>
        <p style={{textAlign:'center', color:'#888', fontSize:'12px', marginBottom:'20px'}}>Adding as: <b style={{color:'#4a00e0'}}>{currentUserName}</b></p>

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Main Category</label>
        <select
          name="businessType"
          value={formData.businessType}
          onChange={handleInputChange}
          className="input-field"
        >
          {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Sub Category</label>
        <AutocompleteInput
          value={formData.category}
          onChange={val => setFormData(prev => ({ ...prev, category: val }))}
          options={categories}
          placeholder="e.g. Katan Silk, Serum, Hand made jewellery..."
          className="input-field"
        />

        <label style={{ fontSize: '12px', color: '#888', display: 'block', marginBottom: '4px' }}>Product Name</label>
        <AutocompleteInput
          value={formData.name}
          onChange={handleNameChange}
          onSelect={handleNameChange}
          options={itemNameOptions}
          placeholder="-- Select Item --"
          className="input-field"
        />

        <div style={{display:'flex', gap:'10px'}}>
          <input type="number" min="0" name="costPrice" value={formData.costPrice} onChange={handleInputChange} placeholder="Unit Cost (৳)" className="input-field" />
          <input type="number" min="0" name="sellingPrice" value={formData.sellingPrice} onChange={handleInputChange} placeholder="Selling Price (৳)" className="input-field" />
        </div>

        <div style={{display:'flex', gap:'10px'}}>
          <input type="number" min="1" name="quantity" value={formData.quantity} onChange={handleInputChange} placeholder="Quantity (Pcs)" className="input-field" />
          <input type="number" min="0" onKeyDown={e => e.key === '-' && e.preventDefault()} name="otherCost" value={formData.otherCost} onChange={handleInputChange} placeholder="Conveyance / Transport (৳)" className="input-field" />
        </div>

        <div style={{display:'flex', gap:'10px', marginTop:'10px'}}>
          <input type="text" name="supplierName" value={formData.supplierName} onChange={handleInputChange} placeholder="Supplier Name (e.g. Emon)" className="input-field" />
          <input type="text" name="supplierPhone" value={formData.supplierPhone} onChange={handleInputChange} placeholder="Phone Number" className="input-field" />
        </div>

        {qtyNum > 0 && baseRateNum > 0 && (
          <div style={{ background: '#f9f9f9', padding: '10px 15px', borderRadius: '10px', fontSize: '12px', color: '#333', margin: '15px 0' }}>
            <p style={{ margin: '2px 0' }}>Base: ৳ {formatTk(baseRateNum)} | Convence/Pcs: ৳ {formatTk(perUnitExtra)}</p>
            <p style={{ margin: '2px 0', fontWeight: 'bold', color: '#4a00e0' }}>1 Pcs Effective Cost: ৳ {formatTk(finalUnitCost)}</p>
            <p style={{ margin: '2px 0', fontWeight: 'bold', color: '#27ae60' }}>Total Cost: ৳ {formatTk(finalTotalCost)}</p>
          </div>
        )}

        <button type="submit" disabled={loading} className="btn-confirm" style={{ width: '150px', display: 'block', margin: '20px auto 0' }}>
          {loading ? "Saving..." : "Save Purchase Entry"}
        </button>
      </form>

      {previewPurchase && (
        <div style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000, padding: '20px', overflowY: 'auto' }}>
          <div style={{ background:'#fff', padding:'30px', borderRadius:'25px', width:'450px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)', position:'relative', maxHeight:'95vh', display: 'flex', flexDirection: 'column' }}>
            <button onClick={()=>setPreviewPurchase(null)} style={{ position:'absolute', top:'15px', right:'15px', border:'none', background:'#ffeded', color:'red', width:'30px', height:'30px', borderRadius:'50%', cursor:'pointer', fontWeight:'bold', zIndex: 10 }}>X</button>

            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '5px', background: 'white' }}>
              <div style={{ textAlign:'center', marginBottom:'15px' }}>
                <h1 style={{ margin:0, color:'#4a00e0', fontSize:'22px' }}>PB KIZUNA COMPANY LIMITED</h1>
                <p style={{ margin:'4px 0', fontSize:'11px', fontWeight:'bold' }}>H-10, R-2, Section-2, Block-D, <br/>Aftabnagar, Dhaka-1212</p>
                <p style={{ margin:0, fontSize:'11px' }}>Mobile: 01884-711844</p>
                <div style={{ borderBottom:'1px dashed #000', margin:'12px 0' }}></div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', letterSpacing: '1px' }}>PURCHASE VOUCHER / INVOICE</h4>
              </div>
              <div style={{ fontSize:'12px', marginBottom:'12px' }}>
                <p style={{ margin: '2px 0' }}><b>Added By:</b> {previewPurchase.addedBy}</p>
                <p style={{ margin: '2px 0' }}><b>Date:</b> {previewPurchase.dateTime}</p>
                <p style={{ margin: '2px 0' }}><b>Purchase ID:</b> #{previewPurchase.purchaseId}</p>
                <p style={{ margin: '2px 0' }}><b>Supplier:</b> {previewPurchase.supplierName} {previewPurchase.supplierPhone !== 'N/A' ? `(${previewPurchase.supplierPhone})` : ''}</p>
                <div style={{ borderBottom:'1px dashed #000', margin:'8px 0' }}></div>
              </div>
              <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #eee' }}>
                    <th align='left' style={{ paddingBottom: '4px' }}>ITEM</th>
                    <th align='center' style={{ paddingBottom: '4px' }}>QTY</th>
                    <th align='right' style={{ paddingBottom: '4px' }}>UNIT COST</th>
                    <th align='right' style={{ paddingBottom: '4px' }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{padding:'5px 0'}}><b>{previewPurchase.productName}</b></td>
                    <td align='center'>{previewPurchase.quantity}</td>
                    <td align='right'>{formatTk(previewPurchase.finalUnitCost)}</td>
                    <td align='right'>{formatTk(previewPurchase.totalCost)}</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ borderTop:'1px solid #eee', marginTop:'10px', paddingTop:'8px', textAlign:'right', fontSize:'12px' }}>
                <p style={{ margin: '3px 0' }}>Base Rate (x{previewPurchase.quantity}): ৳ {formatTk(previewPurchase.baseRate * previewPurchase.quantity)}</p>
                {previewPurchase.otherCost > 0 && (
                  <p style={{ margin: '3px 0' }}>Transport / Other Cost: +৳ {formatTk(previewPurchase.otherCost)}</p>
                )}
                <h3 style={{ margin:'6px 0', fontSize: '14px' }}>Total Cost: ৳ {formatTk(previewPurchase.totalCost)}</h3>
              </div>
              <p style={{ textAlign:'center', marginTop:'15px', fontSize:'11px', fontWeight:'bold' }}>Thank you!</p>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginTop:'15px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
              <button onClick={()=>setPreviewPurchase(null)} style={{ padding:'10px', borderRadius:'10px', border:'1px solid #ddd', background: '#fff', cursor:'pointer', fontSize:'12px' }}>Close</button>
              <button onClick={handlePrintPurchase} className="btn-confirm" style={{ padding:'10px', fontSize:'13px' }}>PRINT PURCHASE VOUCHER 🖨️</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchase;